import React from 'react';
import { ThemeProvider, createTheme, Box, Grid, Card, CardContent, Typography, CircularProgress, IconButton } from '@mui/material';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { MonetizationOn, People, PersonAdd, ShoppingCart, Mic, MoreVert } from '@mui/icons-material';

// Dark theme matching the image
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0a1a2e',
      paper: '#1e2a44',
    },
    text: {
      primary: '#ffffff',
      secondary: '#a3bffa',
    },
    primary: {
      main: '#3b82f6',
    },
    success: {
      main: '#22c55e',
    },
    error: {
      main: '#ef4444',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: { fontWeight: 500 },
    h6: { fontWeight: 400 },
    body2: { color: '#a3bffa' },
  },
});

// Sample data for charts
const salesData = [
  { month: 'Jan', sales: 400 },
  { month: 'Feb', sales: 300 },
  { month: 'Mar', sales: 500 },
  { month: 'Apr', sales: 200 },
  { month: 'May', sales: 400 },
  { month: 'Jun', sales: 600 },
  { month: 'Jul', sales: 300 },
  { month: 'Aug', sales: 500 },
  { month: 'Sep', sales: 400 },
  { month: 'Oct', sales: 300 },
  { month: 'Nov', sales: 500 },
  { month: 'Dec', sales: 400 },
];

const barData = [
  { name: 'Users', value: 32794 },
  { name: 'Clicks', value: 24000 },
  { name: 'Sales', value: 24005 },
  { name: 'Items', value: 320 },
];

const lineData = [
  { month: 'Jan', revenue: 200 },
  { month: 'Feb', revenue: 300 },
  { month: 'Mar', revenue: 250 },
  { month: 'Apr', revenue: 400 },
  { month: 'May', revenue: 350 },
  { month: 'Jun', revenue: 500 },
];

const pieData = [
  { name: 'Category A', value: 400 },
  { name: 'Category B', value: 300 },
  { name: 'Category C', value: 200 },
];

const COLORS = ['#3b82f6', '#60a5fa', '#93c5fd'];

const SampleDashboard = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ p: 3, backgroundColor: 'background.default', height: 'auto' }}>
        {/* Header Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <MonetizationOn sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">$53,000</Typography>
                  <Typography variant="body2" color="success.main">+55%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <People sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">2,300</Typography>
                  <Typography variant="body2" color="success.main">+3%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAdd sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">+3,462</Typography>
                  <Typography variant="body2" color="error.main">-2%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: 'background.paper' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <ShoppingCart sx={{ color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">$173,000</Typography>
                  <Typography variant="body2" color="success.main">+8%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {/* Welcome Card */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', position: 'relative', height: '200px' }}>
                  <CardContent>
                    <Typography variant="h5">Welcome back, Mark Johnson</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>Glad to see you again! Ask me anything.</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <IconButton sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        <Mic sx={{ color: 'white' }} />
                      </IconButton>
                      <Typography variant="body2" sx={{ ml: 1 }}>Tap to record</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Satisfaction Rate */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ backgroundColor: 'background.paper', height: '200px' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">Satisfaction Rate</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>From all projects</Typography>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress variant="determinate" value={95} size={100} thickness={5} sx={{ color: 'primary.main' }} />
                      <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h5">95%</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Sales Overview */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">Sales Overview</Typography>
                      <Typography variant="body2" color="error.main">(-) 5% more in 2021</Typography>
                    </Box>
                    <AreaChart width={600} height={300} data={salesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#a3bffa" />
                      <YAxis stroke="#a3bffa" />
                      <Tooltip />
                      <Area type="monotone" dataKey="sales" stroke="#3b82f6" fill="url(#colorSales)" />
                      <defs>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              {/* Referral Tracking */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: 'background.paper', height: '200px' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">Referral Tracking</Typography>
                      <IconButton>
                        <MoreVert sx={{ color: 'white' }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2">Invited</Typography>
                        <Typography variant="h6">145 people</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2">Bonus</Typography>
                        <Typography variant="h6">1,465</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ position: 'relative', display: 'inline-flex', mt: 2, ml: '25%' }}>
                      <CircularProgress variant="determinate" value={93} size={80} thickness={5} sx={{ color: '#22c55e' }} />
                      <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h6">9.3</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>Total Score</Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Bar Chart */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant="h6">Active Users</Typography>
                    <BarChart width={300} height={200} data={barData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" stroke="#a3bffa" />
                      <YAxis stroke="#a3bffa" />
                      <Tooltip />
                      <Bar dataKey="value" fill="#3b82f6" barSize={30} />
                    </BarChart>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* New Bottom Row for Revenue and Category Charts */}
          <Grid item xs={12}>
            <Grid container spacing={2} justifyContent="center">
              {/* New Line Chart */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant="h6">Revenue Trend</Typography>
                    <LineChart width={350} height={250} data={lineData} margin={{ top: 5, right: 15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#a3bffa" interval="preserveStartEnd" />
                      <YAxis stroke="#a3bffa" />
                      <Tooltip />
                      <Line type="monotone" dataKey="revenue" stroke="#60a5fa" />
                    </LineChart>
                  </CardContent>
                </Card>
              </Grid>

              {/* New Pie Chart */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant="h6">Category Distribution</Typography>
                    <PieChart width={150} height={250} margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={40} fill="#8884d8" dataKey="value" label>
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default SampleDashboard;